import React from 'react'

import { Box, Button, NumberBadge, Text } from '@factorialco/deprecated-design-system'
import {
  CompetencyCellItemFragment,
  JobCatalogLevelFragment,
  LevelExpectationFragment,
} from 'generated/resources/graphql'
import { groupBy, orderBy } from 'lodash'

import i18n from 'modules/core/lib/i18n'

import DraftLevelModal from 'modules/job_catalog/components/DraftLevelModal'
import type { Role } from 'modules/job_catalog/hooks/useRole'

import { CompetencyCellItem } from './CompetencyCellItem'
import { Expectation } from './Expectation'
import LevelEmployees from './LevelEmployees'
import * as styles from './index.vanilla.css'

const UNGROUPED_KEY = 'competencies-without-group'

interface Props {
  levels: Array<JobCatalogLevelFragment>
  competencies: Array<CompetencyCellItemFragment>
  expectations: Array<LevelExpectationFragment>
  role: Role
  refetch: () => void
}

const CareerPathTable = ({ levels, competencies, expectations, role, refetch }: Props) => {
  const [showLevelModal, setShowLevelModal] = React.useState(false)

  const competenciesByGroup = groupBy(
    competencies,
    (competency) => competency.competencyGroup?.name || UNGROUPED_KEY
  )
  const orderedCompetencyGroups = orderBy(
    Object.keys(competenciesByGroup),
    (groupName) => groupName === UNGROUPED_KEY || groupName
  )

  // https://www.w3.org/WAI/EO/Drafts/tutorials/tables/id-headers/#example-2-table-with-three-headers-related-to-each-data-cel
  return (
    <>
      <table className={styles.table}>
        <thead>
          <tr>
            <td></td>
            {levels.map((level) => (
              <th key={level.id} id={`level-${level.id}`} scope="col" className={styles.th}>
                <Box
                  borderRadius={{ all: 'abs008' }}
                  border={{ all: { color: 'grey400', style: 'solid', width: 's2' } }}
                  padding="s16"
                  gap="s8"
                  height="full"
                >
                  <Text.Normal weight="medium" ellipsis>
                    {level.name}
                  </Text.Normal>
                  <LevelEmployees levelId={level.id} limit={3} />
                </Box>
              </th>
            ))}
            <th scope="col" className={styles.th}>
              <Box
                borderRadius={{ all: 'abs008' }}
                border={{ all: { color: 'grey400', style: 'dashed', width: 's2' } }}
                padding="s12"
                gap="s8"
                height="full"
                width="s240"
                alignItems="center"
              >
                <Button
                  type="secondary"
                  aria-label={i18n.t('job_catalog.career_path.add_job_level')}
                  iconName="add"
                  onClick={() => setShowLevelModal(true)}
                />
                <Text.Normal weight="medium" ellipsis>
                  {i18n.t('job_catalog.career_path.add_job_level')}
                </Text.Normal>
              </Box>
            </th>
          </tr>
        </thead>
        <tbody>
          {orderedCompetencyGroups.map((groupName) => {
            const groupCompetencies = competenciesByGroup[groupName]

            if (!groupCompetencies) {
              return null
            }

            return (
              <>
                <tr>
                  <th
                    id={`group-${groupName}`}
                    colSpan={levels.length + 1}
                    scope="colgroup"
                    className={styles.th}
                  >
                    <Box flexDirection="row" gap="s8">
                      <Text.Normal weight="medium">
                        {groupName === UNGROUPED_KEY
                          ? i18n.t('job_catalog.career_path.competencies_table.ungrouped')
                          : groupName}
                      </Text.Normal>{' '}
                      <NumberBadge label={groupCompetencies.length} />
                    </Box>
                  </th>
                </tr>

                {groupCompetencies.map((competency) => (
                  <tr key={competency.id}>
                    <th
                      headers={`group-${groupName}`}
                      id={`competency-${competency.id}`}
                      className={styles.th}
                    >
                      <CompetencyCellItem
                        roleId={role.id}
                        competency={competency}
                        onUnassign={() => refetch()}
                      />
                    </th>
                    {levels.map((level) => {
                      const expectation = expectations.find(
                        (exp) =>
                          exp.competencyId === competency.id.toString() && exp.levelId === level.id
                      )

                      return (
                        <td
                          key={`${competency.id}-${level.id}`}
                          headers={`group-${groupName} competency-${competency.id} level-${level.id}`}
                          className={styles.td}
                        >
                          <Box height="s200" maxHeight="s200">
                            <Expectation
                              competencyId={`${competency.id}`}
                              levelId={`${level.id}`}
                              expectation={expectation}
                            />
                          </Box>
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </>
            )
          })}
        </tbody>
      </table>
      {showLevelModal && (
        <DraftLevelModal
          roleId={role.id}
          onSuccess={refetch}
          onClose={() => setShowLevelModal(false)}
        />
      )}
    </>
  )
}

export default CareerPathTable
